/**
 * Typed WebSocket client with:
 *  - Automatic exponential-backoff reconnection (max 30 s)
 *  - Sequence-number gap detection (emits 'resync' event)
 *  - Typed send/receive using @playground/shared message schemas
 *  - EventEmitter-style API (on / off / emit)
 */

import {
  ServerMessageSchema,
  type ClientMessage,
  type ServerMessage,
} from '@playground/shared';
import { apiFetch, wsUrl } from './api.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type EventMap = {
  /** Fired when the WebSocket connection is established. */
  open: [];
  /** Fired when the connection closes (may reconnect). */
  close: [code: number, reason: string];
  /** Fired on an unrecoverable error before final close. */
  error: [err: Event];
  /** Fired for every valid message received from the server. */
  message: [msg: ServerMessage];
  /** Fired when a gap in sequence numbers is detected. */
  resync: [lastSeq: number, receivedSeq: number];
  /** Fired when reconnection attempts are exhausted. */
  give_up: [];
};

type Listener<K extends keyof EventMap> = (...args: EventMap[K]) => void;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const INITIAL_RETRY_MS = 500;
const MAX_RETRY_MS = 30_000;
const MAX_ATTEMPTS = 10;

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

export interface SocketOptions {
  roomId: string;
  token: string;
  /** Defaults to `/ws` (resolved relative to the current host). */
  path?: string;
}

export class PlaygroundSocket {
  private ws: WebSocket | null = null;
  private readonly roomId: string;
  private readonly token: string;
  private readonly path: string;

  private listeners = new Map<string, Set<Listener<keyof EventMap>>>();

  private retryCount = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  /** The last seq number received from the server. */
  private lastSeq = -1;
  /** Monotonically increasing seq for messages this client sends. */
  private outgoingSeq = 0;

  constructor(options: SocketOptions) {
    this.roomId = options.roomId;
    this.token = options.token;
    this.path = options.path ?? '/ws';
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /** Open the connection. Safe to call multiple times (idempotent). */
  connect(): void {
    if (this.ws && this.ws.readyState <= WebSocket.OPEN) return;
    // Exchange the JWT for a one-time ticket so the token never appears in the
    // WebSocket URL (and therefore never in server logs or browser history).
    void this.fetchTicketAndOpen();
  }

  private async fetchTicketAndOpen(): Promise<void> {
    if (this.destroyed) return;
    try {
      const res = await apiFetch('/api/ws-ticket', {
        method: 'POST',
        headers: { Authorization: `Bearer ${this.token}` },
      });
      if (!res.ok) {
        this.emit('give_up');
        return;
      }
      const { ticket } = await res.json() as { ticket: string };
      if (!this.destroyed) this.openSocket(ticket);
    } catch {
      // Network error during ticket fetch — treat like a fatal WS error
      this.emit('give_up');
    }
  }

  /**
   * Send a typed message to the server.
   * Throws if the socket is not currently open.
   */
  send(type: ClientMessage['type'], payload: Record<string, unknown> = {}): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('Socket is not open. Cannot send message.');
    }

    const msg: ClientMessage = {
      type,
      roomId: this.roomId,
      seq: this.outgoingSeq++,
      ts: Date.now(),
      // Cast: payload may be undefined for ping
      ...(Object.keys(payload).length > 0 ? { payload: payload as never } : {}),
    } as ClientMessage;

    this.ws.send(JSON.stringify(msg));
  }

  /** Permanently close the connection and stop reconnection attempts. */
  destroy(): void {
    this.destroyed = true;
    this.clearRetryTimer();
    if (this.ws) {
      this.ws.close(1000, 'Client destroyed');
      this.ws = null;
    }
    this.listeners.clear();
  }

  // -------------------------------------------------------------------------
  // EventEmitter
  // -------------------------------------------------------------------------

  on<K extends keyof EventMap>(event: K, listener: Listener<K>): this {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(listener as Listener<keyof EventMap>);
    return this;
  }

  off<K extends keyof EventMap>(event: K, listener: Listener<K>): this {
    this.listeners.get(event)?.delete(listener as Listener<keyof EventMap>);
    return this;
  }

  once<K extends keyof EventMap>(event: K, listener: Listener<K>): this {
    const wrapper = (...args: EventMap[K]) => {
      this.off(event, wrapper as Listener<K>);
      (listener as (...a: EventMap[K]) => void)(...args);
    };
    return this.on(event, wrapper as Listener<K>);
  }

  private emit<K extends keyof EventMap>(event: K, ...args: EventMap[K]): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const listener of set) {
      (listener as (...a: EventMap[K]) => void)(...args);
    }
  }

  // -------------------------------------------------------------------------
  // Internal — connection management
  // -------------------------------------------------------------------------

  private buildUrl(ticket: string): string {
    const base = wsUrl(this.path);
    const params = new URLSearchParams({ roomId: this.roomId, ticket });
    return `${base}?${params.toString()}`;
  }

  private openSocket(ticket: string): void {
    const url = this.buildUrl(ticket);
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.addEventListener('open', () => {
      this.retryCount = 0;
      this.emit('open');
    });

    ws.addEventListener('message', (event: MessageEvent<string>) => {
      this.handleMessage(event.data);
    });

    ws.addEventListener('error', (event) => {
      this.emit('error', event);
    });

    ws.addEventListener('close', (event) => {
      this.emit('close', event.code, event.reason);
      // 4000–4999 are application-level fatal codes (auth failure, room not found, etc.)
      // Do not reconnect — it won't recover without user action.
      if (!this.destroyed && event.code < 4000) {
        this.scheduleReconnect();
      } else if (!this.destroyed && event.code >= 4000) {
        this.emit('give_up');
      }
    });
  }

  private handleMessage(raw: string): void {
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      console.warn('[PlaygroundSocket] Received non-JSON message; ignoring.');
      return;
    }

    const result = ServerMessageSchema.safeParse(parsed);
    if (!result.success) {
      console.warn('[PlaygroundSocket] Message failed schema validation:', result.error.issues);
      return;
    }

    const msg = result.data;

    // Sequence gap detection — skip out-of-band message types (e.g. chat)
    if (msg.type !== 'chat_message') {
      if (this.lastSeq >= 0 && msg.seq !== this.lastSeq + 1) {
        this.emit('resync', this.lastSeq, msg.seq);
      }
      this.lastSeq = msg.seq;
    }

    this.emit('message', msg);
  }

  private scheduleReconnect(): void {
    if (this.destroyed) return;
    if (this.retryCount >= MAX_ATTEMPTS) {
      this.emit('give_up');
      return;
    }

    const delay = Math.min(
      INITIAL_RETRY_MS * Math.pow(2, this.retryCount),
      MAX_RETRY_MS,
    );
    this.retryCount++;

    this.clearRetryTimer();
    this.retryTimer = setTimeout(() => {
      if (!this.destroyed) {
        void this.fetchTicketAndOpen();
      }
    }, delay);
  }

  private clearRetryTimer(): void {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  // -------------------------------------------------------------------------
  // Convenience helpers
  // -------------------------------------------------------------------------

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  sendAction(action: unknown): void {
    this.send('action', { action });
  }

  sendPing(): void {
    this.send('ping', {});
  }
}
